import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { fmtNum, fmtPct, fmtCurrency, fmtDuration, fmtDelta } from "./format";
import WidgetIntelligence from "./WidgetIntelligence";
import FunnelDrillDown from "./FunnelDrillDown";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Card = ({ title, subtitle, children, right, testid }) => (
  <div className="p-4 rounded bg-[#0F0F0F] border border-[#2A2A2A]" data-testid={testid}>
    <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
      <div>
        <h4 className="text-sm font-semibold text-white">{title}</h4>
        {subtitle && <p className="text-[11px] text-[#71717A] mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </div>
    {children}
  </div>
);

const Sparkline = ({ rows, valueKey = "sessions", height = 30 }) => {
  if (!rows || rows.length === 0) return <div className="h-[30px]" />;
  const values = rows.map((r) => Number(r[valueKey]) || 0);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const points = values.map((v, i) => {
    const x = (i / Math.max(values.length - 1, 1)) * 100;
    const y = ((max - v) / range) * 100;
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ height, width: "100%" }}>
      <polyline points={points} fill="none" stroke="#FFC53D" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

const DeltaBadge = ({ delta }) => {
  if (!delta) return null;
  const color = delta.dir === "up" ? "#86EFAC" : "#FF6666";
  return (
    <span className="text-[10px] font-mono" style={{ color }}>
      {delta.dir === "up" ? "▲" : "▼"} {delta.abs}%
    </span>
  );
};

const flagColor = {
  high_traffic_low_conversion: { bg: "#F59E0B22", color: "#FCD34D", label: "High traffic · Low CR" },
  high_bounce: { bg: "#FF444422", color: "#FF8080", label: "High bounce" },
  low_engagement: { bg: "#F9731622", color: "#FB923C", label: "Low engagement" },
  winning_page: { bg: "#10B98122", color: "#6EE7B7", label: "Winner" },
};

const useGetCached = (clientId, section, params, deps = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/intelligence/${clientId}/behaviour/${section}`, {
        params: { ...params, force_refresh: force },
      });
      setData(res.data);
      setUpdatedAt(res.data?.updated_at);
    } catch (e) {
      setError(e.response?.data?.detail || `Failed to load ${section}`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => { load(false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, deps);

  return { data, loading, error, reload: () => load(true), updatedAt };
};

// ============== 3A Traffic Overview ==============
const TrafficOverview = ({ clientId, startDate, endDate }) => {
  const { data, loading, error, reload, updatedAt } = useGetCached(
    clientId, "traffic-overview", { start_date: startDate, end_date: endDate, compare_previous: true },
    [clientId, startDate, endDate]
  );

  if (error) return <Card title="3A · Traffic Overview" testid="ga-traffic-overview"><div className="text-xs text-red-400">{error}</div></Card>;
  if (loading || !data) return <Card title="3A · Traffic Overview" testid="ga-traffic-overview"><div className="text-xs text-[#71717A]">Loading…</div></Card>;

  const t = data.totals || {};
  const p = data.previous || {};
  const tiles = [
    ["Sessions", t.sessions, p.sessions, "num"],
    ["Users", t.activeUsers, p.activeUsers, "num"],
    ["New Users", t.newUsers, p.newUsers, "num"],
    ["Engagement Rate", t.engagementRate, p.engagementRate, "pct"],
    ["Bounce Rate", t.bounceRate, p.bounceRate, "pct"],
    ["Avg Session", t.averageSessionDuration, p.averageSessionDuration, "dur"],
    ["Page Views", t.screenPageViews, p.screenPageViews, "num"],
  ];
  return (
    <Card
      title="3A · Traffic Overview"
      subtitle={`Behaviour KPIs vs previous period · GA4 · cached ${updatedAt ? new Date(updatedAt).toLocaleString() : "—"}`}
      right={<button onClick={reload} className="text-[10px] px-2 py-0.5 rounded border border-[#3A3A3A] text-[#A1A1AA]" data-testid="ga-traffic-resync">Resync</button>}
      testid="ga-traffic-overview"
    >
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        {tiles.map(([label, cur, prev, kind]) => (
          <div key={label} className="p-2 rounded bg-[#1A1A1A] border border-[#2A2A2A]">
            <div className="text-[10px] uppercase text-[#71717A]">{label}</div>
            <div className="text-sm font-mono text-white">
              {kind === "pct" ? fmtPct(cur) : kind === "dur" ? fmtDuration(cur) : fmtNum(cur, { compact: true, decimals: 0 })}
            </div>
            <DeltaBadge delta={fmtDelta(cur, prev)} />
          </div>
        ))}
      </div>
      <div className="mt-3">
        <div className="text-[10px] text-[#71717A] mb-1">Sessions sparkline</div>
        <Sparkline rows={data.sparkline || []} valueKey="sessions" />
      </div>
    </Card>
  );
};

// ============== 3B Channel Attribution ==============
const ChannelAttribution = ({ clientId, startDate, endDate, currency }) => {
  const { data, loading, error, reload, updatedAt } = useGetCached(
    clientId, "channel-attribution", { start_date: startDate, end_date: endDate },
    [clientId, startDate, endDate]
  );
  const [expanded, setExpanded] = useState(null);

  if (error) return <Card title="3B · Channel Attribution" testid="ga-channel-attribution"><div className="text-xs text-red-400">{error}</div></Card>;
  if (loading || !data) return <Card title="3B · Channel Attribution" testid="ga-channel-attribution"><div className="text-xs text-[#71717A]">Loading…</div></Card>;

  const channels = data.channels?.rows || [];
  const drill = data.source_medium?.rows || [];

  return (
    <Card
      title="3B · Channel Attribution"
      subtitle="Sessions, transactions & revenue by GA4 default channel group (behaviour revenue — NOT for billing)"
      right={<button onClick={reload} className="text-[10px] px-2 py-0.5 rounded border border-[#3A3A3A] text-[#A1A1AA]" data-testid="ga-channel-resync">Resync</button>}
      testid="ga-channel-attribution"
    >
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-[#71717A]">
            <tr>
              <th className="text-left py-1">Channel</th>
              <th className="text-right">Sessions</th>
              <th className="text-right">Users</th>
              <th className="text-right">Transactions</th>
              <th className="text-right">Revenue (GA4)</th>
              <th className="text-right">CR</th>
              <th className="text-right">Engagement</th>
              <th className="text-right">Avg Session</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {channels.map((r, i) => {
              const ch = r.sessionDefaultChannelGroup || "(unassigned)";
              const isOpen = expanded === ch;
              const drillRows = drill.filter((x) => (x.sessionDefaultChannelGroup || "") === r.sessionDefaultChannelGroup);
              return (
                <React.Fragment key={i}>
                  <tr className="border-t border-[#2A2A2A]">
                    <td className="py-1 text-white">{ch}</td>
                    <td className="text-right font-mono">{fmtNum(r.sessions, { compact: true, decimals: 0 })}</td>
                    <td className="text-right font-mono">{fmtNum(r.activeUsers, { compact: true, decimals: 0 })}</td>
                    <td className="text-right font-mono">{fmtNum(r.transactions, { compact: true, decimals: 0 })}</td>
                    <td className="text-right font-mono text-white">{fmtCurrency(r.totalRevenue, currency)}</td>
                    <td className="text-right font-mono" style={{ color: r.conversion_rate > 0.02 ? "#86EFAC" : "#A1A1AA" }}>
                      {fmtPct(r.conversion_rate)}
                    </td>
                    <td className="text-right font-mono">{fmtPct(r.engagementRate)}</td>
                    <td className="text-right font-mono">{fmtDuration(r.averageSessionDuration)}</td>
                    <td className="text-right">
                      <button
                        onClick={() => setExpanded(isOpen ? null : ch)}
                        className="text-[10px] text-[#A1A1AA] hover:text-white"
                      >
                        {isOpen ? "Hide" : "Drill"}
                      </button>
                    </td>
                  </tr>
                  {isOpen && drillRows.length > 0 && (
                    <tr className="bg-[#161616]">
                      <td colSpan="9" className="p-2">
                        <div className="text-[10px] text-[#71717A] mb-1">Source / Medium breakdown</div>
                        <table className="w-full text-[11px]">
                          <thead className="text-[#52525B]">
                            <tr>
                              <th className="text-left">Source</th><th className="text-left">Medium</th>
                              <th className="text-right">Sessions</th><th className="text-right">Transactions</th>
                              <th className="text-right">Revenue</th><th className="text-right">CR</th>
                            </tr>
                          </thead>
                          <tbody>
                            {drillRows.slice(0, 15).map((d, j) => (
                              <tr key={j} className="border-t border-[#2A2A2A]">
                                <td className="text-[#E4E4E7]">{d.sessionSource}</td>
                                <td className="text-[#A1A1AA]">{d.sessionMedium}</td>
                                <td className="text-right font-mono">{fmtNum(d.sessions, { compact: true, decimals: 0 })}</td>
                                <td className="text-right font-mono">{fmtNum(d.transactions, { compact: true, decimals: 0 })}</td>
                                <td className="text-right font-mono">{fmtCurrency(d.totalRevenue, currency)}</td>
                                <td className="text-right font-mono">{fmtPct(d.conversion_rate)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {channels.length === 0 && (
              <tr><td colSpan="9" className="text-center py-4 text-[#71717A]">No channel data.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-[#71717A] mt-2">Cached {updatedAt ? new Date(updatedAt).toLocaleString() : "—"}</div>
    </Card>
  );
};

// ============== 3C Landing Pages ==============
const LandingPages = ({ clientId, startDate, endDate, currency }) => {
  const { data, loading, error, reload, updatedAt } = useGetCached(
    clientId, "landing-pages", { start_date: startDate, end_date: endDate, limit: 75 },
    [clientId, startDate, endDate]
  );
  const [pageTypeFilter, setPageTypeFilter] = useState("all");

  if (error) return <Card title="3C · Landing Page Intelligence" testid="ga-landing"><div className="text-xs text-red-400">{error}</div></Card>;
  if (loading || !data) return <Card title="3C · Landing Page Intelligence" testid="ga-landing"><div className="text-xs text-[#71717A]">Loading…</div></Card>;

  const rows = (data.rows || []).filter((r) => pageTypeFilter === "all" || r.page_type === pageTypeFilter);
  const filters = [
    ["all", "All"], ["product", "Products"], ["collection", "Collections"], ["home", "Home"], ["campaign", "Campaigns"], ["other", "Other"]
  ];

  return (
    <Card
      title="3C · Landing Page Intelligence"
      subtitle="Which entry pages convert, leak, or win"
      right={<button onClick={reload} className="text-[10px] px-2 py-0.5 rounded border border-[#3A3A3A] text-[#A1A1AA]" data-testid="ga-landing-resync">Resync</button>}
      testid="ga-landing"
    >
      <div className="flex flex-wrap gap-1 mb-2">
        {filters.map(([k, l]) => (
          <button
            key={k}
            onClick={() => setPageTypeFilter(k)}
            className={`text-[11px] px-2 py-1 rounded ${pageTypeFilter === k ? "bg-[#FFEB00] text-black" : "border border-[#3A3A3A] text-[#A1A1AA]"}`}
            data-testid={`ga-landing-filter-${k}`}
          >
            {l}
          </button>
        ))}
      </div>
      <div className="overflow-x-auto max-h-[420px]">
        <table className="w-full text-xs">
          <thead className="text-[#71717A] sticky top-0 bg-[#0F0F0F]">
            <tr>
              <th className="text-left py-1">Page</th>
              <th className="text-left">Type</th>
              <th className="text-right">Sessions</th>
              <th className="text-right">Bounce</th>
              <th className="text-right">Engagement</th>
              <th className="text-right">Avg Session</th>
              <th className="text-right">Revenue</th>
              <th className="text-right">CR</th>
              <th>Flags</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-[#2A2A2A]">
                <td className="py-1 text-white truncate max-w-[280px]" title={r.landingPagePlusQueryString}>{r.landingPagePlusQueryString}</td>
                <td className="text-[#A1A1AA] uppercase text-[10px]">{r.page_type}</td>
                <td className="text-right font-mono">{fmtNum(r.sessions, { compact: true, decimals: 0 })}</td>
                <td className="text-right font-mono" style={{ color: r.bounceRate > 0.7 ? "#FF8080" : "#A1A1AA" }}>{fmtPct(r.bounceRate)}</td>
                <td className="text-right font-mono">{fmtPct(r.engagementRate)}</td>
                <td className="text-right font-mono">{fmtDuration(r.averageSessionDuration)}</td>
                <td className="text-right font-mono text-white">{fmtCurrency(r.totalRevenue, currency)}</td>
                <td className="text-right font-mono" style={{ color: r.conversion_rate > 0.02 ? "#86EFAC" : "#A1A1AA" }}>{fmtPct(r.conversion_rate)}</td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {(r.ai_flags || []).map((f) => {
                      const c = flagColor[f] || { bg: "#52525B22", color: "#A1A1AA", label: f };
                      return (
                        <span key={f} className="text-[9px] px-1.5 py-0.5 rounded font-medium" style={{ background: c.bg, color: c.color }}>
                          {c.label}
                        </span>
                      );
                    })}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan="9" className="text-center py-4 text-[#71717A]">No pages.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-[#71717A] mt-2">Cached {updatedAt ? new Date(updatedAt).toLocaleString() : "—"}</div>
    </Card>
  );
};

// ============== 3D Devices ==============
const Devices = ({ clientId, startDate, endDate, currency }) => {
  const { data, loading, error, reload, updatedAt } = useGetCached(
    clientId, "devices", { start_date: startDate, end_date: endDate },
    [clientId, startDate, endDate]
  );
  if (error) return <Card title="3D · Device Intelligence" testid="ga-devices"><div className="text-xs text-red-400">{error}</div></Card>;
  if (loading || !data) return <Card title="3D · Device Intelligence" testid="ga-devices"><div className="text-xs text-[#71717A]">Loading…</div></Card>;

  const dRows = data.devices?.rows || [];
  const bRows = data.browsers?.rows || [];
  const flags = data.flags || [];

  return (
    <Card
      title="3D · Device + Browser Intelligence"
      subtitle="Where does conversion friction live? (mobile vs desktop, in-app browser detection)"
      right={<button onClick={reload} className="text-[10px] px-2 py-0.5 rounded border border-[#3A3A3A] text-[#A1A1AA]" data-testid="ga-devices-resync">Resync</button>}
      testid="ga-devices"
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {dRows.map((r, i) => (
          <div key={i} className="p-3 rounded bg-[#1A1A1A] border border-[#2A2A2A]">
            <div className="text-[10px] uppercase text-[#71717A]">{r.deviceCategory}</div>
            <div className="text-lg font-mono text-white">{fmtNum(r.sessions, { compact: true, decimals: 0 })} <span className="text-[10px] text-[#71717A]">sessions</span></div>
            <div className="grid grid-cols-2 gap-1 mt-2 text-[11px]">
              <div className="text-[#71717A]">CR: <span className="text-white font-mono">{fmtPct(r.conversion_rate)}</span></div>
              <div className="text-[#71717A]">Bounce: <span className="text-white font-mono">{fmtPct(r.bounceRate)}</span></div>
              <div className="text-[#71717A]">Engage: <span className="text-white font-mono">{fmtPct(r.engagementRate)}</span></div>
              <div className="text-[#71717A]">Revenue: <span className="text-white font-mono">{fmtCurrency(r.totalRevenue, currency)}</span></div>
            </div>
          </div>
        ))}
      </div>
      {flags.length > 0 && (
        <div className="mt-3 space-y-1" data-testid="ga-devices-flags">
          {flags.map((f, i) => (
            <div key={i} className="text-[11px] p-2 rounded" style={{ background: f.severity === "high" ? "#FF444422" : "#F59E0B22", color: f.severity === "high" ? "#FF8080" : "#FCD34D" }}>
              <strong>⚠ {f.type.replace(/_/g, " ")}</strong> — {f.message}
            </div>
          ))}
        </div>
      )}
      <div className="mt-4">
        <div className="text-xs font-semibold text-white mb-1">Top Browsers</div>
        <div className="overflow-x-auto max-h-[300px]">
          <table className="w-full text-xs">
            <thead className="text-[#71717A] sticky top-0 bg-[#0F0F0F]">
              <tr>
                <th className="text-left py-1">Browser</th>
                <th className="text-left">Device</th>
                <th className="text-right">Sessions</th>
                <th className="text-right">Bounce</th>
                <th className="text-right">Engagement</th>
                <th className="text-right">Revenue</th>
                <th className="text-right">CR</th>
              </tr>
            </thead>
            <tbody>
              {bRows.slice(0, 20).map((r, i) => (
                <tr key={i} className="border-t border-[#2A2A2A]">
                  <td className="py-1 text-white">
                    {r.browser}
                    {r.is_in_app && <span className="ml-1 text-[9px] px-1 py-0.5 rounded" style={{ background: "#FF444422", color: "#FF8080" }}>in-app</span>}
                  </td>
                  <td className="text-[#A1A1AA]">{r.deviceCategory}</td>
                  <td className="text-right font-mono">{fmtNum(r.sessions, { compact: true, decimals: 0 })}</td>
                  <td className="text-right font-mono">{fmtPct(r.bounceRate)}</td>
                  <td className="text-right font-mono">{fmtPct(r.engagementRate)}</td>
                  <td className="text-right font-mono">{fmtCurrency(r.totalRevenue, currency)}</td>
                  <td className="text-right font-mono" style={{ color: r.conversion_rate > 0.02 ? "#86EFAC" : "#A1A1AA" }}>{fmtPct(r.conversion_rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="text-[10px] text-[#71717A] mt-2">Cached {updatedAt ? new Date(updatedAt).toLocaleString() : "—"}</div>
    </Card>
  );
};

// ============== 3E Funnel ==============
const FunnelBars = ({ steps, onStepClick, activeStep }) => {
  if (!steps || steps.length === 0) return <div className="text-[11px] text-[#71717A]">No funnel data.</div>;
  const top = Math.max(...steps.map((s) => Number(s.users) || 0), 1);
  return (
    <div className="space-y-1">
      {steps.map((s) => {
        const w = Math.max(((Number(s.users) || 0) / top) * 100, 2);
        const isActive = activeStep === s.event;
        return (
          <button
            type="button"
            key={s.event}
            onClick={() => onStepClick && onStepClick(s.event)}
            className={`w-full grid grid-cols-[140px_1fr_70px] gap-2 items-center text-left rounded transition-colors ${isActive ? "ring-1 ring-[#FFEB00]" : "hover:bg-[#1A1A1A]"} px-1 py-0.5`}
            data-testid={`ga-funnel-step-${s.event}`}
          >
            <div className="text-[11px] text-[#A1A1AA] truncate">{s.event}</div>
            <div className="bg-[#1A1A1A] rounded h-6 relative overflow-hidden">
              <div className="h-full" style={{ width: `${w}%`, background: isActive ? "#FFEB00CC" : "#FFC53D88" }} />
              <div className="absolute inset-0 px-2 flex items-center text-[11px] font-mono text-white">{fmtNum(s.users, { compact: true, decimals: 0 })} users · {fmtNum(s.event_count, { compact: true, decimals: 0 })} events</div>
            </div>
            <div className="text-[10px] text-right font-mono" style={{ color: s.drop_off_pct_from_prev > 50 ? "#FF8080" : s.drop_off_pct_from_prev > 0 ? "#FCD34D" : "#86EFAC" }}>
              {s.drop_off_pct_from_prev != null ? `-${s.drop_off_pct_from_prev}%` : ""}
            </div>
          </button>
        );
      })}
    </div>
  );
};

const Funnel = ({ clientId, startDate, endDate, currency }) => {
  const { data, loading, error, reload, updatedAt } = useGetCached(
    clientId, "funnel", { start_date: startDate, end_date: endDate },
    [clientId, startDate, endDate]
  );
  const [view, setView] = useState("overall");
  const [drill, setDrill] = useState(null); // { dimension, value }

  if (error) return <Card title="3E · Funnel Intelligence" testid="ga-funnel"><div className="text-xs text-red-400">{error}</div></Card>;
  if (loading || !data) return <Card title="3E · Funnel Intelligence" testid="ga-funnel"><div className="text-xs text-[#71717A]">Loading…</div></Card>;

  // The Overall and device variants are intentionally NOT drillable as a
  // "source" because they aren't standalone GA channels. Mobile and Desktop
  // are drillable via the deviceCategory dimension; the rest are channels.
  const variants = [
    ["overall", "Overall", data.overall, null],
    ["mobile", "Mobile", data.mobile, "Mobile"],
    ["desktop", "Desktop", data.desktop, "Desktop"],
  ];
  const channelKeys = data.by_channel ? Object.keys(data.by_channel) : [];

  const handleTabClick = (key, sourceLabel) => {
    setView(key);
    if (sourceLabel) {
      setDrill({ dimension: "source", value: sourceLabel });
    } else {
      setDrill(null); // "Overall" closes any open drill-down
    }
  };

  const handleStepClick = (event) => {
    // Toggle off if same step clicked again
    if (drill?.dimension === "step" && drill.value === event) {
      setDrill(null);
    } else {
      setDrill({ dimension: "step", value: event });
    }
  };

  return (
    <Card
      title="3E · Funnel Intelligence"
      subtitle="session_start → view_item → add_to_cart → begin_checkout → add_payment_info → purchase · click a tab or step to drill in"
      right={<button onClick={reload} className="text-[10px] px-2 py-0.5 rounded border border-[#3A3A3A] text-[#A1A1AA]" data-testid="ga-funnel-resync">Resync</button>}
      testid="ga-funnel"
    >
      <div className="flex flex-wrap gap-1 mb-2">
        {variants.map(([k, l, , sourceLabel]) => (
          <button
            key={k}
            onClick={() => handleTabClick(k, sourceLabel)}
            className={`text-[11px] px-2 py-1 rounded ${view === k ? "bg-[#FFEB00] text-black" : "border border-[#3A3A3A] text-[#A1A1AA]"}`}
            data-testid={`ga-funnel-view-${k}`}
          >
            {l}
          </button>
        ))}
        {channelKeys.map((ch) => (
          <button
            key={ch}
            onClick={() => handleTabClick(ch, ch)}
            className={`text-[11px] px-2 py-1 rounded ${view === ch ? "bg-[#FFEB00] text-black" : "border border-[#3A3A3A] text-[#A1A1AA]"}`}
            data-testid={`ga-funnel-view-${ch}`}
          >
            {ch}
          </button>
        ))}
      </div>
      <FunnelBars
        steps={variants.find((v) => v[0] === view)?.[2] || data.by_channel?.[view] || []}
        onStepClick={handleStepClick}
        activeStep={drill?.dimension === "step" ? drill.value : null}
      />
      {(data.warnings || []).length > 0 && (
        <div className="mt-3 space-y-1" data-testid="ga-funnel-warnings">
          {data.warnings.map((w, i) => (
            <div key={i} className="text-[11px] p-2 rounded" style={{ background: "#F59E0B22", color: "#FCD34D" }}>
              <strong>⚠ {w.type.replace(/_/g, " ")}</strong> — {w.message}
            </div>
          ))}
        </div>
      )}
      {drill && (
        <FunnelDrillDown
          clientId={clientId}
          dimension={drill.dimension}
          value={drill.value}
          startDate={startDate}
          endDate={endDate}
          currency={currency}
          onClose={() => setDrill(null)}
        />
      )}
      <div className="text-[10px] text-[#71717A] mt-2">Cached {updatedAt ? new Date(updatedAt).toLocaleString() : "—"}</div>
    </Card>
  );
};

// ============== 3F Source Quality ==============
const SourceQuality = ({ clientId, startDate, endDate, currency }) => {
  const { data, loading, error, reload, updatedAt } = useGetCached(
    clientId, "source-quality", { start_date: startDate, end_date: endDate, limit: 40 },
    [clientId, startDate, endDate]
  );
  if (error) return <Card title="3F · Source Quality Matrix" testid="ga-source-quality"><div className="text-xs text-red-400">{error}</div></Card>;
  if (loading || !data) return <Card title="3F · Source Quality Matrix" testid="ga-source-quality"><div className="text-xs text-[#71717A]">Loading…</div></Card>;

  const rows = data.rows || [];
  const tierColor = { high: "#86EFAC", medium: "#FCD34D", low: "#FF8080" };

  return (
    <Card
      title="3F · Source Quality Matrix"
      subtitle="Which traffic sources are actually high-quality? (engagement + bounce + CR composite score)"
      right={<button onClick={reload} className="text-[10px] px-2 py-0.5 rounded border border-[#3A3A3A] text-[#A1A1AA]" data-testid="ga-quality-resync">Resync</button>}
      testid="ga-source-quality"
    >
      <div className="overflow-x-auto max-h-[420px]">
        <table className="w-full text-xs">
          <thead className="text-[#71717A] sticky top-0 bg-[#0F0F0F]">
            <tr>
              <th className="text-left py-1">Source</th>
              <th className="text-left">Medium</th>
              <th className="text-right">Sessions</th>
              <th className="text-right">Bounce</th>
              <th className="text-right">Engagement</th>
              <th className="text-right">Avg Session</th>
              <th className="text-right">AOV</th>
              <th className="text-right">CR</th>
              <th className="text-right">Quality</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-[#2A2A2A]">
                <td className="py-1 text-white">{r.sessionSource}</td>
                <td className="text-[#A1A1AA]">{r.sessionMedium}</td>
                <td className="text-right font-mono">{fmtNum(r.sessions, { compact: true, decimals: 0 })}</td>
                <td className="text-right font-mono">{fmtPct(r.bounceRate)}</td>
                <td className="text-right font-mono">{fmtPct(r.engagementRate)}</td>
                <td className="text-right font-mono">{fmtDuration(r.averageSessionDuration)}</td>
                <td className="text-right font-mono">{fmtCurrency(r.aov, currency)}</td>
                <td className="text-right font-mono">{fmtPct(r.conversion_rate)}</td>
                <td className="text-right font-mono" style={{ color: tierColor[r.quality_tier] || "#A1A1AA" }}>
                  {r.quality_tier?.toUpperCase()} · {r.quality_score}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan="9" className="text-center py-4 text-[#71717A]">No data.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-[#71717A] mt-2">Cached {updatedAt ? new Date(updatedAt).toLocaleString() : "—"}</div>
    </Card>
  );
};

// ============== Main Behaviour Section ==============
const BehaviourIntelligence = ({ clientId, currency, gaConnected, dateRange, trafficIntel, loadingIntel }) => {
  const [internalRange, setInternalRange] = useState("30daysAgo");
  // External dateRange (from PerformanceIntelligence parent) overrides internal selector
  const startDate = dateRange?.start_date || internalRange;
  const endDate = dateRange?.end_date || "today";

  if (!gaConnected) {
    return (
      <div className="p-6 rounded bg-[#0F0F0F] border border-[#2A2A2A]" data-testid="ga-not-connected">
        <h3 className="text-sm font-semibold text-white">Google Analytics 4 not connected</h3>
        <p className="text-xs text-[#71717A] mt-1">
          Connect a GA4 property on the <strong>Client Edit</strong> page to unlock Behaviour Intelligence —
          traffic, channel attribution, landing pages, devices, funnel & source quality.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="behaviour-intelligence">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-white">Customer Behaviour Intelligence</h2>
          <p className="text-[11px] text-[#71717A]">
            Source: GA4 · Behaviour + Attribution truth · NOT used for billing
            {dateRange && <span className="text-[#FFEB00] ml-2">· {dateRange.label}</span>}
          </p>
        </div>
        {!dateRange && (
          <select
            className="input-default text-xs py-1 px-2"
            value={internalRange}
            onChange={(e) => setInternalRange(e.target.value)}
            data-testid="behaviour-range-select"
          >
            <option value="7daysAgo">Last 7 days</option>
            <option value="30daysAgo">Last 30 days</option>
            <option value="90daysAgo">Last 90 days</option>
          </select>
        )}
      </div>
      <TrafficOverview clientId={clientId} startDate={startDate} endDate={endDate} />
      <WidgetIntelligence
        widget={trafficIntel}
        label={loadingIntel ? "AI Intelligence · Generating…" : "AI Intelligence · Traffic"}
        testid="widget-intel-traffic"
      />
      <ChannelAttribution clientId={clientId} startDate={startDate} endDate={endDate} currency={currency} />
      <Funnel clientId={clientId} startDate={startDate} endDate={endDate} currency={currency} />
      <LandingPages clientId={clientId} startDate={startDate} endDate={endDate} currency={currency} />
      <Devices clientId={clientId} startDate={startDate} endDate={endDate} currency={currency} />
      <SourceQuality clientId={clientId} startDate={startDate} endDate={endDate} currency={currency} />
    </div>
  );
};

export default BehaviourIntelligence;
